<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    filterCities,
    getCityList,
    getCountryList,
    markerStyles,
    type City,
    type Country,
    type MapEngine,
    type MapClickEvent,
  } from '@atlasguess/engine';
  import { createAudio, type AudioPlayer } from '@atlasguess/audio';
  import {
    applyEngineConfig,
    buildCityPrompt,
    CONTINENT_CAMERAS,
    formatDistance,
    haversineKm,
    Picker,
    resolveFilterSlug,
    verdictForKm,
    zoomForCountry,
    type CityPracticeConfig,
  } from '@atlasguess/games';
  import Prompt from '../framework/Prompt.svelte';
  import NextButton from '../framework/NextButton.svelte';
  import FilterPicker from '../framework/FilterPicker.svelte';
  import PracticeHistoryPanel from '../framework/PracticeHistoryPanel.svelte';
  import { displayUnit } from '../../lib/units';
  import { soundEnabled } from '../../lib/sound';
  import { recordRun } from '../../lib/runs';
  import {
    getHistory,
    recordGuessLocally,
    type GuessRecord,
  } from '../../lib/practiceHistory';

  interface Props {
    config: CityPracticeConfig;
    /** Live engine instance, owned by ModeFrame. Guaranteed non-null. */
    engine: MapEngine;
    /**
     * Optional URL filter slug (`/play/cities-world/practice/europe` or
     * `.../practice/japan`). Continent slugs translate to a `continents`
     * filter, country slugs to a `countryIsos` filter, both layered on
     * top of `config.cityFilter`. v1: pool stays at the mode's tier
     * ceiling — no auto-bump when filtered. (Bhutan-style "tiny country
     * with no tier-1 cities" case is acceptable for v1; a follow-up
     * mode will surface lower tiers explicitly.)
     */
    filter?: string;
  }

  let { config, engine, filter }: Props = $props();

  type Phase = 'loading' | 'awaiting-guess' | 'showing-result';

  let phase = $state<Phase>('loading');
  let currentCity = $state<City | null>(null);
  let lastDistanceKm = $state<number | null>(null);
  let lastGuess = $state<{ lat: number; lon: number } | null>(null);
  /**
   * Verdict locked at click time (not derived) so the phrase doesn't
   * reroll on every reactive re-render. Reset to '' between rounds;
   * populated in handleMapClick.
   */
  let lastVerdict = $state('');
  /** Prior guesses at the CURRENT city, loaded fresh per round. Drives
   *  the history panel and the past-guess markers on the globe. */
  let pastGuesses = $state<GuessRecord[]>([]);
  /**
   * Sequence number bumped on each round transition. The async history
   * fetch closes over this; on resolution we only apply the result if
   * the sequence still matches — guards against a slow Round-1 fetch
   * landing after the player has already advanced to Round-2.
   */
  let roundSeq = 0;
  // $state so the prompt's $derived re-evaluates when countries finish loading.
  let countriesByIso = $state<Map<string, Country>>(new Map());
  let picker: Picker<City> | null = null;
  let audio: AudioPlayer | undefined;
  let restoreEngineConfig: (() => void) | null = null;
  /**
   * Countries to show in the FilterPicker's per-country group. Derived
   * from the BASE pool (config filter, no URL slug) so the dropdown
   * stays the same regardless of what the player is currently filtered
   * to — they can always switch to any country reachable from the mode's
   * tier ceiling. Sorted alphabetically by display name.
   *
   * v1: only countries that have at least one city in the base pool.
   * Bhutan-style "tiny country with no tier-1 cities" simply won't appear.
   * Future: when we ship modes that lower the tier ceiling for those
   * cases, the dropdown will gain those countries automatically.
   */
  let pickerCountries = $state<Country[]>([]);

  /** Marker IDs for the current round — cleaned up between rounds + on unmount. */
  const CLICK_MARKER_ID = '_mode_click';
  const LOCATION_MARKER_ID = '_mode_location';
  const MARKER_LAYER = '_mode_round';
  /** Separate layer for purple past-guess markers — cleared per round. */
  const HISTORY_MARKER_LAYER = '_mode_history';

  /** Two-line prompt for the current city — { primary, secondary }. */
  const prompt = $derived(
    currentCity ? buildCityPrompt(currentCity, countriesByIso) : null,
  );

  /** Result line shown after a guess: "143 mi". Reads from the user's
   *  persisted unit setting so toggling miles/km in /account immediately
   *  updates the next guess's display. */
  const resultText = $derived(
    lastDistanceKm === null ? '' : formatDistance(lastDistanceKm, $displayUnit),
  );


  onMount(() => {
    // Gate the AudioContext on the user's saved setting at construction
    // time — a muted user shouldn't pay for context init on first click.
    audio = createAudio({ enabled: $soundEnabled });
    restoreEngineConfig = applyEngineConfig(engine, config.layers);

    // Hook the engine's click stream — every globe click flows through here.
    // We process it only when accepting input (Phase = 'awaiting-guess');
    // mid-result clicks are ignored so the user doesn't accidentally restart
    // the round before reading the result.
    engine.setOnMapClick(handleMapClick);

    // Lazy-load cities for this mode's tier ceiling, then pre-load countries
    // for prompt rendering. We DO load borders (skip-fills, skip-hit-tester)
    // so the post-guess fade-in can show country boundaries without paying
    // for the triangulation + PIP precompute we'd never use.
    void initialize();
  });

  // Live-track the user's sound setting so toggling it in /account
  // silences any in-flight sequence and stops future ones immediately.
  $effect(() => {
    audio?.setEnabled($soundEnabled);
  });

  async function initialize(): Promise<void> {
    const [cities, countries] = await Promise.all([
      getCityList({ tierMax: config.tierMax }),
      getCountryList(),
      engine.loadBorders({ buildFills: false, buildHitTester: false }),
    ]);
    countriesByIso = new Map(countries.map((c) => [c.iso_a2, c]));

    // Base pool (no URL slug applied) — used to derive which countries
    // appear in the FilterPicker's per-country group. Computed once
    // before slug-narrowing so the dropdown is a stable list regardless
    // of the player's current filter selection.
    const basePool = filterCities(cities, {
      ...config.cityFilter,
      countryByIso: countriesByIso,
    });
    const isosInBasePool = new Set(basePool.map((c) => c.country_iso));
    pickerCountries = countries
      .filter((c) => isosInBasePool.has(c.iso_a2))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Compose the URL filter slug onto the mode's base cityFilter.
    // Continent slug → `continents`; country slug → `countryIsos`.
    // Slug resolution wants the country dataset (already loaded above).
    const resolved = resolveFilterSlug(filter, countries);
    const filtered = filterCities(cities, {
      ...config.cityFilter,
      ...(resolved.continentName ? { continents: [resolved.continentName] } : {}),
      ...(resolved.countryIso ? { countryIsos: [resolved.countryIso] } : {}),
      countryByIso: countriesByIso,
    });
    if (filtered.length === 0) {
      console.warn('[atlas] city filter returned no cities; mode cannot start');
      return;
    }

    // Animate the camera to the filtered region so the player opens
    // looking at the relevant slice of the globe. Continent slug → fixed
    // CONTINENT_CAMERAS entry; country slug → centroid + size-based zoom.
    // No-op when filter is empty (engine starts at world view).
    if (filter && CONTINENT_CAMERAS[filter]) {
      engine.setCamera({ ...CONTINENT_CAMERAS[filter], animate: true });
    } else if (resolved.countryIso) {
      const country = countriesByIso.get(resolved.countryIso);
      if (country) {
        engine.setCamera({
          lat: country.centroid[1],
          lon: country.centroid[0],
          zoom: zoomForCountry(country),
          animate: true,
        });
      }
    }

    picker = new Picker(filtered, (c) => c.id);
    nextRound();
  }

  function nextRound(): void {
    if (!picker) return;
    engine.markers.clear(MARKER_LAYER);
    engine.markers.clear(HISTORY_MARKER_LAYER);
    engine.arcs.clear();
    // Hide borders for the prompt phase — they'd give away country shapes
    // before the user has guessed. Tween back to 0 (auto-animated 400ms).
    engine.layers.outline.setBordersOpacity(0);
    audio?.cancel();
    lastDistanceKm = null;
    lastGuess = null;
    lastVerdict = '';
    pastGuesses = [];
    currentCity = picker.next();
    phase = 'awaiting-guess';

    // Bump the seq + capture for the closure so a slow fetch can detect
    // it landed after the round changed and bail out before applying.
    const seq = ++roundSeq;
    const cityForRound = currentCity;
    if (cityForRound) {
      void loadHistoryData(cityForRound.id, seq);
    }
  }

  /**
   * Fetch past guesses for this city ahead of the player's click so the
   * post-guess panel can render with no latency. Markers are NOT painted
   * here — past-guess markers are a hint after the answer, not before
   * (otherwise the history dots would tell a player roughly where the
   * city is before they've earned the reveal).
   */
  async function loadHistoryData(targetId: string, seq: number): Promise<void> {
    const guesses = await getHistory(targetId);
    if (seq !== roundSeq) return; // user moved on; abort
    pastGuesses = guesses;
  }

  /**
   * Pick a small subset of past guesses to render as markers — at most
   * 5 most-recent plus the all-time best. Cap exists because a player
   * who's revisited a city 50 times produces an unreadable cloud of
   * dots; the recent-and-best slice keeps the surface communicative
   * (you can see your rough trend + your high water mark) without
   * burying the answer marker. Spatial declustering (merging dots
   * that overlap) is a follow-up if even the slice still feels muddy.
   */
  function selectMarkersToRender(guesses: GuessRecord[]): GuessRecord[] {
    const withCoords = guesses.filter(
      (g): g is GuessRecord & { lat: number; lon: number } =>
        g.lat !== null && g.lon !== null,
    );
    if (withCoords.length === 0) return [];

    const recent = withCoords.slice(-5);
    const best = withCoords.reduce((a, b) => (a.score_km < b.score_km ? a : b));

    // Dedupe: if best is already in recent, recent alone covers both.
    if (recent.includes(best)) return recent;
    return [best, ...recent];
  }

  function handleMapClick(event: MapClickEvent): void {
    if (phase !== 'awaiting-guess' || !currentCity || !audio) return;
    const guess = { lat: event.lat, lon: event.lon };
    const target = { lat: currentCity.lat, lon: currentCity.lon };
    const km = haversineKm(guess, target);
    lastDistanceKm = km;
    lastGuess = guess;
    // Lock the verdict at click time — re-deriving would reroll the
    // phrase on every reactive update, which reads as jittery.
    lastVerdict = verdictForKm(km);
    phase = 'showing-result';

    // Practice = one row per guess. Each round is its own run_key — the
    // server upserts on (user, run_key, target), so a unique key per
    // guess never collides with the next. lat/lon flow through so the
    // history panel + markers can render past attempts.
    void recordRun({
      mode_id: config.id,
      format: 'practice',
      run_key: `${config.id}-${Date.now()}`,
      rounds: [{
        target_id: currentCity.id,
        score: km,
        score_unit: 'km',
        lat: guess.lat,
        lon: guess.lon,
      }],
    });
    // Guest path — server-side game_run isn't written, so we keep our
    // own copy in localStorage. recordGuessLocally is a no-op for
    // authed users.
    recordGuessLocally(currentCity.id, {
      score_km: km,
      lat: guess.lat,
      lon: guess.lon,
      played_at: new Date().toISOString(),
    });

    engine.markers.add({
      id: CLICK_MARKER_ID,
      position: guess,
      style: markerStyles.click,
      layer: MARKER_LAYER,
    });
    engine.markers.add({
      id: LOCATION_MARKER_ID,
      position: target,
      style: markerStyles.location,
      layer: MARKER_LAYER,
    });

    // Past-guess markers reveal in lockstep with the answer marker —
    // showing them earlier would hint at the city's rough location.
    for (const g of selectMarkersToRender(pastGuesses)) {
      engine.markers.add({
        id: `_mode_history_${g.played_at}`,
        position: { lat: g.lat as number, lon: g.lon as number },
        style: markerStyles.history,
        layer: HISTORY_MARKER_LAYER,
      });
    }

    // Reveal borders so the player can see if their guess at least landed
    // in the right country. Auto-animated tween from 0 → 1 over ~400ms.
    engine.layers.outline.setBordersOpacity(1.0);

    // Audio + arc kicked off in lockstep — same duration so the ding lands
    // exactly when the arc reaches the answer.
    const durationMs = config.audio.travelDurationMsForKm(km);
    const dingFrequencies = config.audio.dingFrequenciesForKm(km);
    audio.playSequence({ durationMs, dingFrequencies });
    engine.arcs.draw({
      from: guess,
      to: target,
      durationMs,
      removeOnComplete: false,
    });
  }

  onDestroy(() => {
    engine.setOnMapClick(undefined);
    engine.markers.clear(MARKER_LAYER);
    engine.markers.clear(HISTORY_MARKER_LAYER);
    engine.arcs.clear();
    audio?.cancel();
    audio?.dispose();
    restoreEngineConfig?.();
  });
</script>

{#if config.userMayChangeFilter && phase !== 'loading'}
  <!-- City picker: continents + per-country group. The country list is
       restricted to countries that have at least one city in the BASE
       pool (config tier ceiling, no URL slug) so the dropdown stays
       stable across filter changes. -->
  <FilterPicker
    scopeSlug={`${config.scope.entity}-${config.scope.region}`}
    format={config.format}
    activeSlug={filter}
    countries={pickerCountries}
    region={config.scope.region}
  />
{/if}

{#if phase !== 'loading' && prompt}
  <Prompt
    primary={prompt.primary}
    secondary={prompt.secondary}
    secondaryFlagUrl={prompt.flagFile ? `/${prompt.flagFile}` : undefined}
    active={phase === 'awaiting-guess'}
  />
{/if}

{#if phase === 'showing-result' && lastDistanceKm !== null && lastGuess && currentCity}
  <div class="round-result round-result--card">
    <div class="round-result__distance">{resultText}</div>
    <div class="round-result__verdict">{lastVerdict}</div>
    <PracticeHistoryPanel
      {pastGuesses}
      currentGuess={{
        score_km: lastDistanceKm,
        lat: lastGuess.lat,
        lon: lastGuess.lon,
        played_at: new Date().toISOString(),
      }}
      units={$displayUnit}
    />
    <NextButton onclick={nextRound} spaceHotkey />
  </div>
{/if}
